// Cuándo y cómo se avisa de que el plan se acaba. Sin React y sin red.
//
// LA REGLA ESTÁ EN EL SERVIDOR, y aquí sólo se dice en voz alta. `app/domain/acceso.py` es
// quien decide qué se abre y qué no; esto no abre ni cierra nada, sólo cuenta los días que
// quedan y elige la frase. Si alguna vez discrepan, manda el servidor: lo peor que puede
// pasar aquí es un aviso a destiempo, y lo peor allí es una lección abierta de más.
//
// LOS SIETE DÍAS ESTÁN ESCRITOS EN LOS DOS SITIOS y tienen que ser el mismo número. Lo vigila
// `tools/check_acceso_avisos.py`: si el servidor avisa con una semana y la pantalla con tres
// días, el alumno se entera cuatro días tarde de algo que se decidió avisar antes.

export const VENTANA_AVISO_DIAS = 7;

// Y cuántos días se sigue diciendo DESPUÉS de que se acabe. Dos semanas: las suficientes para
// que le dé tiempo a hablarlo en casa, y no tantas como para que la banda se convierta en
// parte del decorado. Pasadas, la puerta cerrada sigue explicándose cuando se topa con ella
// —eso no caduca—, pero deja de haber una banda encima de todas las pantallas.
export const VENTANA_DESPUES_DIAS = 14;

/** El día, a secas, de un ISO que puede venir corto o largo. `null` si no se entiende. */
function dia(valor) {
  if (!valor || typeof valor !== "string" || valor.length < 10) return null;
  const [a, m, d] = valor.slice(0, 10).split("-").map(Number);
  if (!a || !m || !d) return null;
  // A mediodía UTC a propósito: construir la fecha a medianoche y restar en un huso al oeste
  // devuelve el día anterior, y entonces «te queda 1 día» se convierte en «se acabó ayer».
  return Date.UTC(a, m - 1, d, 12);
}

/**
 * Días que quedan. Negativo si ya pasó, 0 si es hoy, `null` si no hay fecha.
 *
 * El último día se juega entero: `hasta` es el último día CON plan, no el primero sin él.
 * Es la misma frase que hay escrita en `acceso.py`, y a propósito.
 */
export function quedan(hasta, hoy = new Date()) {
  const fin = dia(hasta);
  if (fin === null) return null;
  const ahora = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 12);
  return Math.round((fin - ahora) / 86400000);
}

/** ¿Toca avisar? Sólo a quien tiene algo que perder, y sólo dentro de la ventana. */
export function hayQueAvisar(plan, hasta, hoy = new Date()) {
  const dias = quedan(hasta, hoy);
  if (dias === null || !plan || plan === "free") return false;
  return dias >= 0 && dias <= VENTANA_AVISO_DIAS;
}

/**
 * Qué se le dice, y qué NO se le dice.
 *
 * NO SE DICE «PERDERÁS TU PROGRESO», porque es mentira: la cuenta se queda, la estrella se
 * queda, el XP se queda y las lecciones hechas siguen hechas. Lo que se cierra es lo que
 * abría el plan. Un aviso que asusta de más consigue que se lea una vez y se ignore el resto,
 * y de paso es falso, que ya es motivo suficiente.
 */
export function comoSeDice(dias) {
  if (dias === null || dias < 0) return null;
  if (dias === 0) return "Hoy es el último día de tu acceso completo.";
  if (dias === 1) return "Mañana se acaba tu acceso completo.";
  return `Te quedan ${dias} días de acceso completo.`;
}

/** Lo que se dice cuando ya pasó. Sin dramatismo: lo que hay es una puerta, no una pérdida. */
export const SE_ACABO = "Tu acceso completo se ha terminado.";

/** Lo que se conserva, dicho claro. Va debajo del aviso, siempre. */
export const LO_QUE_SE_QUEDA =
  "Tu cuenta no se cierra: se quedan tu estrella, tu XP, tus recuerdos y las lecciones que "
  + "ya has hecho. Lo que deja de estar abierto es el resto del curso.";

/**
 * ¿Ya se acabó? Tenía plan con fecha y la fecha pasó.
 *
 * NO ES LO MISMO QUE «TIENE EL PLAN GRATIS», y por eso hace falta distinguirlo. A quien nunca
 * tuvo acceso completo se le enseña lo que puede comprar; a quien lo tuvo y se le acabó hay
 * que contarle antes que **no ha perdido nada**, porque si no lo primero que piensa es que su
 * progreso se ha ido con el plan. Son dos conversaciones distintas con la misma puerta
 * delante, y esa es justo la diferencia que la pantalla no puede deducir del plan a secas:
 * el servidor ya lo ha devuelto a `free`, así que por el plan los dos casos se ven iguales.
 * Lo que los separa es que haya una fecha, y que esté pasada.
 */
export function seAcabo(usuario, hoy = new Date()) {
  const dias = quedan(usuario?.plan_hasta || "", hoy);
  return dias !== null && dias < 0;
}

/** ¿Se le sigue diciendo en la banda de arriba? Sólo durante las dos primeras semanas. */
export function reciente(usuario, hoy = new Date()) {
  const dias = quedan(usuario?.plan_hasta || "", hoy);
  return dias !== null && dias < 0 && dias >= -VENTANA_DESPUES_DIAS;
}

/**
 * Todo junto, que es como lo pide una pantalla.
 *
 * @returns {{avisar: boolean, dias: number|null, frase: string|null, seQueda: string}}
 */
export function aviso(usuario, hoy = new Date()) {
  const plan = usuario?.plan;
  const hasta = usuario?.plan_hasta || "";
  const dias = quedan(hasta, hoy);
  const antes = hayQueAvisar(plan, hasta, hoy);
  const despues = reciente(usuario, hoy);
  return {
    avisar: antes || despues,
    // `acabado` separa las dos conversaciones: contar los días que quedan, o contar que ya
    // pasó. La pantalla no tiene que volver a deducirlo de un número negativo.
    acabado: despues,
    dias,
    frase: antes ? comoSeDice(dias) : (despues ? SE_ACABO : null),
    seQueda: LO_QUE_SE_QUEDA,
  };
}
